import { extractDisplayWeight } from './weightUtils'

export const PIN_KINDS = Object.freeze({
  HOUSE: 'house_pin',
  CUSTOMER: 'customer_pin'
})

const EMBEDDED_PIN_FIELDS = [
  'pinWeight',
  'customerPinWeight',
  'assayLoss',
  'finalPinWeight'
]

const SERIAL_SUFFIX = {
  [PIN_KINDS.HOUSE]: 'HP',
  [PIN_KINDS.CUSTOMER]: 'CP'
}

export function findHousePinForBar(bar, pins) {
  return pinsForBar(bar, pins).find(pin => pin.kind === PIN_KINDS.HOUSE) || null
}

export function findCustomerPinForBar(bar, pins) {
  return pinsForBar(bar, pins).find(pin => pin.kind === PIN_KINDS.CUSTOMER) || null
}

export function findHousePinWeightForBar(bar, pins) {
  return findHousePinForBar(bar, pins)?.grossWeight || null
}

export function findCustomerPinWeightForBar(bar, pins) {
  return findCustomerPinForBar(bar, pins)?.grossWeight || null
}

export function pinsForBar(bar, pins) {
  return (Array.isArray(pins) ? pins : []).filter(pin => pinBelongsToBar(pin, bar))
}

export function pinsOfKind(kind, pins) {
  return (Array.isArray(pins) ? pins : []).filter(pin => pin.kind === kind)
}

export function pinBelongsToBar(pin, bar) {
  if (!pin || !bar) return false
  if (bar.id && pin.refiningLotBarId) return String(pin.refiningLotBarId) === String(bar.id)
  return String(pin.barNumber) === String(bar.barNumber)
}

export function buildPinSerialNumber(barSerialNumber, kind) {
  return barSerialNumber ? `${barSerialNumber}-${SERIAL_SUFFIX[kind]}` : null
}

export function buildPinIdentity(bar, kind) {
  return {
    ...(bar.id ? { refiningLotBarId: bar.id } : {}),
    barNumber: bar.barNumber,
    ...(bar.serialNumber ? { serialNumber: buildPinSerialNumber(bar.serialNumber, kind) } : {}),
    kind
  }
}

export function upsertHousePin(pins, bar, changes) {
  return upsertPin(pins, bar, PIN_KINDS.HOUSE, changes)
}

export function upsertCustomerPin(pins, bar, changes) {
  return upsertPin(pins, bar, PIN_KINDS.CUSTOMER, changes)
}

export function setHousePinWeight(pins, bar, grossWeight) {
  return setPinWeight(pins, bar, PIN_KINDS.HOUSE, grossWeight)
}

export function setCustomerPinWeight(pins, bar, grossWeight) {
  return setPinWeight(pins, bar, PIN_KINDS.CUSTOMER, grossWeight, { assayLoss: null })
}

export function stripEmbeddedPinFields(bar) {
  const canonicalBar = { ...bar }
  EMBEDDED_PIN_FIELDS.forEach(field => delete canonicalBar[field])
  return canonicalBar
}

export function buildDistinctPayload(bars, pins) {
  return {
    bars: (Array.isArray(bars) ? bars : []).map(stripEmbeddedPinFields),
    pins: Array.isArray(pins) ? pins : []
  }
}

function upsertPin(pins, bar, kind, changes) {
  const currentPins = Array.isArray(pins) ? pins : []
  const index = currentPins.findIndex(pin => pinBelongsToBar(pin, bar) && pin.kind === kind)
  const nextPin = {
    ...(index === -1 ? buildPinIdentity(bar, kind) : currentPins[index]),
    ...changes,
    kind
  }

  if (index === -1) return [...currentPins, nextPin]
  return currentPins.map((pin, pinIndex) => pinIndex === index ? nextPin : pin)
}

function setPinWeight(pins, bar, kind, grossWeight, extraChanges = {}) {
  const value = extractDisplayWeight(grossWeight, 'troyOunces')
  if (value === 0 || value === null) {
    return (Array.isArray(pins) ? pins : [])
      .filter(pin => !(pinBelongsToBar(pin, bar) && pin.kind === kind))
  }
  return upsertPin(pins, bar, kind, { grossWeight, ...extraChanges })
}
